import { useCallback } from 'react'

import {
  ConditionBooleanOperator,
  ConditionExpression,
  ConditionFeatureFlag,
  ConditionValue,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DateTimeCustomOption,
  DefaultConditionStatement,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
} from '@makyoapp/makyo-common'

import { globalVariableClient, useGlobalVariableValues } from '@/core/app-builder/global-variables'
import { parseConditionBasedOnOperator } from '@/core/app-builder/parser/condition'
import { parseVariableDateTime } from '@/core/app-builder/parser/datetime'
import { parseProfileData } from '@/core/app-builder/parser/profile-data'
import { parseSingleCellValue } from '@/core/app-builder/parser/single-cell'
import { IDB } from '@/core/db'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

import { useBreakpoint } from '../useBreakpoint'

const breakpointToDeviceMap: Record<string, string> = {
  'mobile-portrait': 'phone',
  'mobile-landscape': 'phone-landscape',
  'tablet-portrait': 'tablet',
  'tablet-landscape': 'tablet-landscape',
  desktop: 'desktop',
  tv: 'tv',
}

function resolveConditionValue(
  condition: DefaultConditionStatement,
  isEnd: boolean,
  dbClient: IDB,
  userEmail?: string,
  language?: string,
  userSpecific?: string,
  detailTableId?: string,
  rowIndex?: number,
  formState?: Record<string, any>,
  device?: string
) {
  switch (condition.flag) {
    // TODO: should be details page
    case ConditionFeatureFlag.CurrentRow: {
      const { col, endCol } = condition
      let colId = isEnd ? endCol : col

      const value = parseSingleCellValue(
        {
          tbl: detailTableId,
          col: colId,
          row: rowIndex,
        },
        dbClient,
        language,
        userSpecific,
        false
      )

      return value
    }
    // TODO: should be details component row
    case ConditionFeatureFlag.ColumnData: {
      const { tbl, endTbl, col, endCol } = condition
      let tblId = isEnd ? endTbl : tbl
      let colId = isEnd ? endCol : col
      const rowIdx = typeof rowIndex === 'number' ? rowIndex : 0

      const value = parseSingleCellValue(
        {
          tbl: tblId,
          col: colId,
          row: rowIdx,
        },
        dbClient,
        language,
        userSpecific,
        false
      )

      return value?.toString()
    }
    case ConditionFeatureFlag.SingleCell: {
      const { tbl, endTbl, col, endCol, row, endRow } = condition
      let tblId = isEnd ? endTbl : tbl
      let colId = isEnd ? endCol : col
      let rowIndex = isEnd ? endRow : row

      const value = parseSingleCellValue(
        {
          tbl: tblId,
          col: colId,
          row: String(Number(rowIndex) - 1),
        },
        dbClient,
        language,
        userSpecific,
        false
      )

      return value?.toString()
    }
    case ConditionFeatureFlag.UserLoggedIn: {
      return !!userEmail
    }
    case ConditionFeatureFlag.CustomText: {
      return isEnd ? condition.endValue : condition.value
    }
    case ConditionFeatureFlag.ProfileData: {
      const profileDataValue = parseProfileData(condition.col, dbClient, userEmail)
      return profileDataValue
    }
    case ConditionFeatureFlag.FormData: {
      const { col } = condition
      return formState?.[col]
    }
    case ConditionFeatureFlag.DateTime: {
      const { value, endValue } = condition
      const dateVal = isEnd ? endValue : value
      return parseVariableDateTime(dateVal as DateTimeCustomOption)
    }
    case ConditionFeatureFlag.Breakpoint: {
      if (condition.value) {
        return breakpointToDeviceMap[condition.value] || 'desktop'
      }
      return device || 'phone'
    }
    case ConditionFeatureFlag.GlobalVariable: {
      const varName = condition.value
      const value = globalVariableClient?.getValue(varName)
      return value ?? ''
    }
    default:
      return null
  }
}

/**
 * Helper function to check if visibility value matches always-hidden pattern
 * Always-hidden pattern: 2 conditions with UserLoggedIn IsFalse AND UserLoggedIn IsTrue
 * IMPORTANT: Must have isAny === false (AND logic) to be always-hidden.
 * If isAny === true (OR logic), it would always evaluate to true (user is either logged in OR not logged in).
 */
function isAlwaysHiddenPattern(visibility: ConditionValue | undefined): boolean {
  if (!visibility || !visibility.conditions) return false

  const { conditions, isAny } = visibility

  // Must be AND logic (isAny === false) for always-hidden pattern
  // Check explicitly for false (not just truthy/falsy)
  if (isAny !== false && isAny !== undefined) return false

  // If isAny is undefined, we still check the pattern but prefer explicit false
  // This handles edge cases where isAny might not be set
  const matchesPattern =
    conditions.length === 2 &&
    conditions[0]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
    conditions[1]?.lhs?.flag === ConditionFeatureFlag.UserLoggedIn &&
    conditions[0]?.operator === ConditionBooleanOperator.IsFalse &&
    conditions[1]?.operator === ConditionBooleanOperator.IsTrue

  // Only consider it always-hidden if pattern matches AND isAny is explicitly false
  // VisibilitySelector always sets isAny: false when creating always-hidden pattern
  const result = matchesPattern && isAny === false
  return result
}

/**
 * @note please be ware of the tableId & rowIndex args,
 *
 * if its used in component, please use `detailTableId` & `detailRowIndex` from component context instead.
 * do not use `activeLayerTableId` & `activeLayerDetailRowIndex` carelessly.
 */
export function checkVisibilityV2(
  visibility: ConditionValue | undefined,
  dbClient: IDB | undefined,
  userEmail: string,
  language: string,
  userSpecific: string,
  tableId?: string,
  rowIndex?: number,
  formState?: Record<string, any>,
  device?: string
) {
  if (!visibility || !dbClient) {
    return true
  }

  // Early return for always-hidden pattern (always return false)
  const isAlwaysHidden = isAlwaysHiddenPattern(visibility)
  if (isAlwaysHidden) {
    return false
  }

  const { isAny = true, conditions = [] } = visibility

  if (conditions.length === 0) {
    return true
  }

  try {
    // Evaluate all conditions
    const conditionResults = conditions.map((condition: ConditionExpression) => {
      const rangeFields = [...NUMERIC_OPERATOR_WITH_RANGE_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS]

      const isRange = rangeFields.includes(condition.operator)

      // parse lhs value
      let lhsValue = resolveConditionValue(
        condition.lhs,
        false,
        dbClient,
        userEmail,
        language,
        userSpecific,
        tableId,
        rowIndex,
        formState,
        device
      )

      // validate lhs value (for array result from lookup, single cell)
      // we will use the first value from the array
      if (Array.isArray(lhsValue)) {
        lhsValue = lhsValue.flat()[0]
      }

      // Get RHS value(s)
      let rhsValue, rhsEndValue
      if (condition.rhs) {
        rhsValue = resolveConditionValue(
          condition.rhs,
          false,
          dbClient,
          userEmail,
          language,
          userSpecific,
          tableId,
          rowIndex,
          formState,
          device
        )

        if (isRange) {
          rhsEndValue = resolveConditionValue(
            condition.rhs,
            true,
            dbClient,
            userEmail,
            language,
            userSpecific,
            tableId,
            rowIndex,
            formState,
            device
          )
        }
      }

      // Construct RHS parameter
      const rhsParam = !isRange
        ? { value: rhsValue }
        : {
            start: rhsValue,
            end: rhsEndValue,
          }

      // Parse and evaluate the condition
      const conditionResult = parseConditionBasedOnOperator(
        lhsValue,
        condition.operator,
        rhsParam,
        true // caseSensitive
      )

      return conditionResult
    })

    // Set visibility based on isAny flag
    return isAny ? conditionResults.some((r) => r) : conditionResults.every((r) => r)
    //
  } catch (error) {
    return true // Default to visible on error
  }
}

interface CheckVisibilityArgs {
  visibility: ConditionValue | undefined
  formState?: Record<string, any>
  layerTableId?: string
  layerRowIndex?: number
}

export function useCheckVisibilityV2() {
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail } = useAppAuthContext()
  const { activeLayerTableId, activeLayerDetailRowIndex } = useActiveLayerContext()
  const { device } = useBreakpoint()

  useGlobalVariableValues()

  const checkVisibility = useCallback(
    ({ visibility, formState, layerTableId, layerRowIndex }: CheckVisibilityArgs) => {
      return checkVisibilityV2(
        visibility,
        dbClient,
        userEmail,
        language,
        userSpecific,
        layerTableId ?? activeLayerTableId,
        layerRowIndex ?? activeLayerDetailRowIndex,
        formState,
        device
      )
    },
    [dbClient, userEmail, language, userSpecific, activeLayerTableId, activeLayerDetailRowIndex, device]
  )

  return { checkVisibility }
}
